import { startTransition, useEffect, useRef, useState } from "react";
import content from "../data/content.json";
import { resolveAssetPath, sortByOrderThenId } from "../utils/content";

const EMPTY_WAVEFORM = [0.14, 0.18, 0.2, 0.24, 0.28, 0.32, 0.28, 0.24, 0.2, 0.18, 0.14];
const IDLE_WAVEFORM = [0.22, 0.28, 0.34, 0.42, 0.56, 0.74, 0.56, 0.42, 0.34, 0.28, 0.22];
const WAVEFORM_BUCKETS = 6;
const WAVEFORM_FPS = 18;

const musicLibrary = [...(content.music ?? [])].sort(sortByOrderThenId);

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const getWaveformFromFrequencies = (frequencyData) => {
  const bucketSize = Math.max(1, Math.floor(frequencyData.length / WAVEFORM_BUCKETS));
  const buckets = Array.from({ length: WAVEFORM_BUCKETS }, (_, index) => {
    const start = index * bucketSize;
    const end =
      index === WAVEFORM_BUCKETS - 1
        ? frequencyData.length
        : Math.min(frequencyData.length, start + bucketSize);

    let total = 0;

    for (let current = start; current < end; current += 1) {
      total += frequencyData[current];
    }

    const average = total / Math.max(1, end - start);
    return clamp(0.18 + (average / 255) * 0.82, 0.16, 1);
  });

  return [...buckets.slice(1).reverse(), buckets[0], ...buckets.slice(1)].map((level) =>
    Number(level.toFixed(3))
  );
};

const getAudioSrc = (track) => {
  if (!track?.file) {
    return "";
  }

  return resolveAssetPath(track.file);
};

function useMusicPlayer() {
  const song =
    musicLibrary.find((track) => track?.is_active && track?.file) ||
    musicLibrary.find((track) => track?.file) ||
    null;
  const [isPlaying, setIsPlaying] = useState(false);
  const [waveform, setWaveform] = useState(EMPTY_WAVEFORM);

  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const frequencyBufferRef = useRef(null);
  const animationFrameRef = useRef(null);
  const lastWaveformUpdateRef = useRef(0);

  const audioSrc = getAudioSrc(song);
  const hasPlayableSong = Boolean(audioSrc);

  useEffect(() => {
    startTransition(() => {
      setWaveform(hasPlayableSong ? IDLE_WAVEFORM : EMPTY_WAVEFORM);
    });
  }, [hasPlayableSong]);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }

      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  const resetWaveform = () => {
    if (animationFrameRef.current) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    lastWaveformUpdateRef.current = 0;

    startTransition(() => {
      setWaveform(hasPlayableSong ? IDLE_WAVEFORM : EMPTY_WAVEFORM);
    });
  };

  const ensureAnalyser = async () => {
    if (sourceNodeRef.current || !audioRef.current) {
      return;
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;

    if (!AudioContextClass) {
      return;
    }

    try {
      const audioContext = new AudioContextClass();
      const analyser = audioContext.createAnalyser();
      const sourceNode = audioContext.createMediaElementSource(audioRef.current);

      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.82;

      sourceNode.connect(analyser);
      analyser.connect(audioContext.destination);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      sourceNodeRef.current = sourceNode;
      frequencyBufferRef.current = new Uint8Array(analyser.frequencyBinCount);
    } catch (error) {
      console.error("Unable to initialize the mobile audio visualizer.", error);
    }
  };

  const startWaveform = () => {
    if (!analyserRef.current || !frequencyBufferRef.current) {
      return;
    }

    if (animationFrameRef.current) {
      window.cancelAnimationFrame(animationFrameRef.current);
    }

    const animate = (timestamp) => {
      if (!analyserRef.current || !frequencyBufferRef.current) {
        animationFrameRef.current = null;
        return;
      }

      analyserRef.current.getByteFrequencyData(frequencyBufferRef.current);

      if (timestamp - lastWaveformUpdateRef.current >= 1000 / WAVEFORM_FPS) {
        const nextWaveform = getWaveformFromFrequencies(frequencyBufferRef.current);
        lastWaveformUpdateRef.current = timestamp;

        startTransition(() => {
          setWaveform(nextWaveform);
        });
      }

      animationFrameRef.current = window.requestAnimationFrame(animate);
    };

    animationFrameRef.current = window.requestAnimationFrame(animate);
  };

  const handleAudioPlay = () => {
    setIsPlaying(true);
    startWaveform();
  };

  const handleAudioPause = () => {
    setIsPlaying(false);
    resetWaveform();
  };

  const togglePlayback = async () => {
    if (!audioRef.current || !hasPlayableSong) {
      return;
    }

    if (isPlaying) {
      audioRef.current.pause();
      return;
    }

    try {
      await ensureAnalyser();

      if (audioContextRef.current?.state === "suspended") {
        await audioContextRef.current.resume();
      }

      await audioRef.current.play();
    } catch (error) {
      console.error(error);
    }
  };

  return {
    audioRef,
    audioSrc,
    handleAudioPause,
    handleAudioPlay,
    hasPlayableSong,
    isPlaying,
    song,
    togglePlayback,
    waveform,
  };
}

export default useMusicPlayer;
